import { ReactElement, useContext } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';

import { SearchMenuFields } from '../../../types';
import { stripHtmlTags } from '../../../utils';
import { useFallbackTranslation } from '../../../utils/hooks';
import { ExtendedSearchResultsItem, SearchContext } from '../providers';
import { Pagination } from './Pagination';
import { SearchTitleHeader } from './SearchTitleHeader';

type ProductResultsProps = {
  searchQuery: string;
  menuItem: SearchMenuFields;
};

type DetailLinkProps = {
  pageUrl: string;
  label: string;
  postfix?: string;
};

const DetailsLink = ({ pageUrl, label, postfix = '' }: DetailLinkProps): ReactElement => {
  const { getURLWithLocalePrefix } = useFallbackTranslation();
  return (
    <>
      {pageUrl ? (
        <a
          href={getURLWithLocalePrefix(pageUrl ? pageUrl + postfix : '/')}
          role="link"
          className="cursor-pointer text-xs font-normal underline underline-offset-4 hover:no-underline"
          aria-label={`Product ${label} link`}
        >
          {label}
        </a>
      ) : null}
    </>
  );
};

export const ProductResults = ({ searchQuery, menuItem }: ProductResultsProps): ReactElement => {
  const { t, getURLWithLocalePrefix } = useFallbackTranslation();
  const { searchResults } = useContext(SearchContext);

  const nextLabel = menuItem.nextLabel?.value || '';
  const previousLabel = menuItem.previousLabel?.value || '';
  const productFoundLabel = menuItem.productsFoundLabel?.value || '';
  const productResultsLabel = menuItem.productResultsLabel?.value || '';
  const productInformationLabel = menuItem.productInformationLabel?.value || '';
  const supportLabel = menuItem.supportLabel?.value || '';
  const numberOfResultsPerPage = menuItem.numberOfResultsPerPage?.value ?? 4;

  const handleNavigation = (item: ExtendedSearchResultsItem) => {
    // todo: replace to router.push after coexistence period.
    window.location.href = getURLWithLocalePrefix(getItemUrl(item));
  };

  const getItemUrl = (item: ExtendedSearchResultsItem): string => {
    return item.isDiscontinued ? item.supportPageUrl : item.pageUrl;
  };

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-col items-center justify-center pt-3">
        <div className="w-full sm:w-4/6 md:w-4/6 lg:w-4/6 xl:w-2/6">
          <SearchTitleHeader
            title={productResultsLabel}
            subtitle={`(${searchResults?.count} ${productFoundLabel})`}
          />
          <ul className="grid grid-cols-1 gap-4 sm:gap-6">
            {searchResults?.items.map((item) => {
              const itemUrl = getItemUrl(item);
              return (
                <li key={`product-${itemUrl}-${item.title}`}>
                  <button
                    onClick={() => handleNavigation(item)}
                    className="flex w-full border-b border-solid border-gray-400 pb-2 no-underline hover:no-underline sm:border-0 sm:pb-0"
                    aria-label={`${t('topmenu.NavigateToProductDetail')} ${item.title}`}
                  >
                    <span className="flex w-full flex-col items-center justify-center rounded-lg p-4 hover:bg-beige-50 hover:shadow-shadow10 sm:flex-row sm:items-center md:gap-10">
                      {/* todo: add translation for label */}
                      <Image
                        aria-label={t('topmenu.ProductImage')}
                        className="flex w-36 justify-center xs:w-32"
                        field={{ src: item.images[0] }}
                        alt={item.title}
                      />
                      <span
                        className="flex w-full flex-col items-start justify-center sm:w-2/3"
                        aria-labelledby={`product-title-${itemUrl}`}
                      >
                        <span className="flex w-full flex-col items-start">
                          {item.isDiscontinued && (
                            <span className="mb-2 rounded bg-gray-100 px-1 pt-[2px] text-xs uppercase">
                              {t('system.Discontinued')}
                            </span>
                          )}
                          <h2
                            className="w-full truncate text-left text-lg font-bold"
                            aria-label={t('topmenu.ProductTitle')}
                          >
                            {item.title}
                          </h2>
                          <p className="w-full truncate text-left text-xs font-bold">
                            {stripHtmlTags(item.uniqueSellingPoint)}
                          </p>
                        </span>
                        <span className="mt-2 flex w-full flex-col flex-wrap gap-4 text-start lg:flex-row lg:gap-3">
                          {!item.isDiscontinued && (
                            <DetailsLink pageUrl={item.pageUrl} label={productInformationLabel} />
                          )}
                          <DetailsLink pageUrl={item.supportPageUrl} label={supportLabel} />
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="mt-4 flex justify-center border-0 pt-2 xs:justify-end sm:border-t sm:border-solid sm:border-gray-400">
            <Pagination
              totalProducts={searchResults.count}
              searchQuery={searchQuery}
              nextLabel={nextLabel}
              previousLabel={previousLabel}
              numberOfResultsPerPage={numberOfResultsPerPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
